/**
 * Pollinations.AI Service - Free Image, Text, and Audio Generation
 * No API keys required - completely free service
 */

import { storageService } from './storageService';
import { databaseService } from './databaseService';
import * as openRouterAdapter from './openRouterToElevenLabsAdapter';
import { imageProcessingService } from './imageProcessingService';

// Pollinations API endpoints
const POLLINATIONS_IMAGE_API = 'https://image.pollinations.ai/prompt';
const POLLINATIONS_AUDIO_API = 'https://audio.pollinations.ai';
// POLLINATIONS_TEXT_API removed - causes CORS errors, use OpenRouter only

export interface PollinationsImageOptions {
  width?: number;
  height?: number;
  seed?: number;
  model?: string;
  style?: string;
  enhance?: boolean;
  private?: boolean;
  // Note: 'safe' parameter removed as it causes image loading issues
}

export interface PollinationsTextOptions {
  model?: 'openai' | 'llama' | 'mistral' | 'gemini';
  maxTokens?: number;
}

export interface PollinationsAudioOptions {
  voice?: string;
  speed?: number;
  model?: string;
  private?: boolean;
}

interface PolllinationsTextToSpeechResponse {
  audioUrl: string;
  duration?: number;
}

interface PolllinationsSpeechToTextResponse {
  text: string;
  confidence?: number;
}

export interface PollinationsGenerationOptions {
  width?: number;
  height?: number;
  seed?: number;
  model?: 'flux' | 'flux-realism' | 'flux-cablyai' | 'flux-anime' | 'flux-3d' | 'any-dark' | 'turbo';
  enhance?: boolean;
  private?: boolean;
  // Note: 'safe' parameter removed as it causes image loading issues
}

export interface ImageGenerationRequest {
  prompt: string;
  style?: string;
  userId: string;
  cacheKey?: string;
  options?: PollinationsGenerationOptions;
}

export interface ImageGenerationResult {
  imageUrl: string;
  cached: boolean;
  generatedAt: Date;
  prompt: string;
}

class PollinationsService {
  private static instance: PollinationsService;
  private baseImageUrl = 'https://image.pollinations.ai';
  private baseTextUrl = 'https://text.pollinations.ai';
  private apiKey = (import.meta as any).env.VITE_POLLINATION_KEY;
  private referrer = 'lucid-cyber-rpg.app';

  static getInstance(): PollinationsService {
    if (!PollinationsService.instance) {
      PollinationsService.instance = new PollinationsService();
    }
    return PollinationsService.instance;
  }

  // Available voices for TTS (from the API documentation)
  private availableVoices = [
    'alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer',
    'coral', 'verse', 'ballad', 'ash', 'sage', 'amuch', 'dan'
  ];

  private defaultOptions: PollinationsGenerationOptions = {
    width: 1024,
    height: 768,
    model: 'flux', // Changed from flux-realism to flux for better stability
    enhance: true,
    private: false // Changed to false to avoid potential 500 errors on free tier
  };

  /**
   * Generate an image using Pollinations AI with caching
   */
  async generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResult> {
    const { prompt, style, userId, cacheKey, options = {} } = request;

    // Create a cache key if not provided
    const finalCacheKey = cacheKey || this.createCacheKey(prompt, style, options);

    try {
      // Check cache first (but don't fail if cache fails)
      let cachedImage: string | null = null;
      try {
        cachedImage = await storageService.getCachedImage(finalCacheKey);
        if (cachedImage) {
          console.log('[PollinationsService] Image found in cache:', finalCacheKey);
          return {
            imageUrl: cachedImage,
            cached: true,
            generatedAt: new Date(),
            prompt
          };
        }
      } catch (cacheError) {
        console.log('[PollinationsService] Cache check failed, proceeding with generation:', cacheError);
      }

      // Enhance the prompt for better results
      console.log('[PollinationsService] Enhancing prompt for better generation...');
      // Simple prompt enhancement without AI
      const enhancedPrompt = `${prompt}, highly detailed, cinematic lighting, professional quality`;
      console.log('[PollinationsService] Enhanced prompt:', enhancedPrompt);

      // Generate new image with enhanced prompt
      console.log('[PollinationsService] Generating new image for prompt:', enhancedPrompt);
      const imageUrl = await this.generateImageFromPollinations(enhancedPrompt, { ...this.defaultOptions, ...options });

      // Skip watermark removal by relying on nologo=true parameter
      const processedImageUrl = imageUrl;

      // Cache the result (cache original URL for consistency, but don't fail if caching fails)
      try {
        await storageService.cacheImage(finalCacheKey, imageUrl, {
          prompt: enhancedPrompt + (style ? ` (Style: ${style})` : ''),
          style,
          userId,
          generatedAt: new Date()
        });
        console.log('[PollinationsService] Image cached successfully');
      } catch (cacheError) {
        console.warn('[PollinationsService] Caching failed but continuing:', cacheError);
        // Don't throw - caching failure shouldn't break image generation
      }

      console.log('[PollinationsService] Image generated and cached:', imageUrl);

      return {
        imageUrl,
        cached: false,
        generatedAt: new Date(),
        prompt: enhancedPrompt
      };

    } catch (error) {
      console.error('[PollinationsService] Error generating image:', error);
      throw new Error(`Failed to generate image: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate thumbnail for story segments or cartridges
   */
  async generateThumbnail(request: {
    prompt: string;
    type: 'story' | 'cartridge' | 'character';
    userId: string;
    id: string;
  }): Promise<ImageGenerationResult> {
    const thumbnailOptions: PollinationsGenerationOptions = {
      width: 256,
      height: 256,
      model: 'flux',
      enhance: true,
      private: true // Keep private from Pollinations public feed
    };

    const enhancedPrompt = this.enhancePromptForThumbnail(request.prompt, request.type);
    const cacheKey = `thumbnail_${request.type}_${request.id}`;

    return this.generateImage({
      prompt: enhancedPrompt,
      userId: request.userId,
      cacheKey,
      options: thumbnailOptions
    });
  }

  /**
   * Generate character portrait
   */
  async generateCharacterPortrait(request: {
    name: string;
    archetype?: string; // Make optional
    traits?: string[]; // Make optional
    userId: string;
    appearanceDescription?: string; // User appearance description
  }): Promise<ImageGenerationResult> {
    const portraitOptions: PollinationsGenerationOptions = {
      width: 512,
      height: 512,
      model: 'flux-realism',
      enhance: true,
      private: true // Keep private from Pollinations public feed
    };

    const prompt = this.createCharacterPrompt(request.name, request.archetype, request.traits, request.appearanceDescription);
    const cacheKey = `portrait_${request.userId}_${request.name.toLowerCase().replace(/\s+/g, '_')}`;

    // Enhance the character prompt with user's appearance description
    // Simple prompt enhancement for character generation
    const enhancedPrompt = `${prompt}, character portrait, detailed, anime style, vibrant colors`;
    console.log('[PollinationsService] Character prompt with user description:', prompt);
    console.log('[PollinationsService] Enhanced character portrait prompt:', enhancedPrompt);

    return this.generateImage({
      prompt: enhancedPrompt,
      userId: request.userId,
      cacheKey,
      options: portraitOptions
    });
  }

  /**
   * Generate story scene image
   */
  async generateStoryImage(request: {
    narrative: string;
    setting: string;
    mood: string;
    userId: string;
    sceneId?: string;
  }): Promise<ImageGenerationResult> {
    const sceneOptions: PollinationsGenerationOptions = {
      width: 1024,
      height: 768,
      model: 'flux-realism',
      enhance: true,
      private: true // Keep private from Pollinations public feed
    };

    const prompt = this.createStoryPrompt(request.narrative, request.setting, request.mood);
    const cacheKey = request.sceneId ? `scene_${request.sceneId}` : this.createCacheKey(prompt, undefined, sceneOptions);

    return this.generateImage({
      prompt,
      userId: request.userId,
      cacheKey,
      options: sceneOptions
    });
  }

  /**
   * Generate image directly from Pollinations API
   */
  private async generateImageFromPollinations(prompt: string, options: PollinationsGenerationOptions): Promise<string> {
    try {
      // Log the full prompt being used for generation
      console.log('[PollinationsService] ===== IMAGE GENERATION START =====');
      console.log('[PollinationsService] Full Prompt:', prompt);
      console.log('[PollinationsService] Options:', JSON.stringify(options, null, 2));

      // Clean and encode the prompt for URL safety
      const cleanPrompt = prompt.replace(/[^a-zA-Z0-9\s,.-]/g, '').trim();
      const encodedPrompt = encodeURIComponent(cleanPrompt);

      console.log('[PollinationsService] Cleaned Prompt:', cleanPrompt);
      console.log('[PollinationsService] Encoded Prompt:', encodedPrompt);

      // Build query parameters
      const params = new URLSearchParams();
      params.append('width', options.width?.toString() || '512');
      params.append('height', options.height?.toString() || '512');
      params.append('model', options.model || 'flux');
      params.append('referrer', this.referrer);

      // Add API key as parameter for authenticated requests
      if (this.apiKey) {
        params.append('key', this.apiKey);
      }

      if (options.enhance !== undefined) params.append('enhance', options.enhance.toString());
      if (options.private !== undefined) params.append('private', options.private.toString());
      // Removed default private=true to avoid 500 errors
      if (options.seed) params.append('seed', options.seed.toString());
      // Note: 'safe' parameter removed completely as it causes image loading issues

      // Correct URL format: /prompt/[encoded_prompt]
      const imageUrl = `${this.baseImageUrl}/prompt/${encodedPrompt}?${params.toString()}`;

      console.log('[PollinationsService] Final URL:', imageUrl);

      // For frontend calls, use referrer instead of Authorization header to avoid CORS
      // API key authentication should be used on backend only
      console.log('[PollinationsService] Using referrer-based authentication for frontend');

      // Test if the image URL is accessible and has content
      const response = await fetch(imageUrl, {
        method: 'HEAD'
      });

      console.log('[PollinationsService] Response status:', response.status);
      console.log('[PollinationsService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`Pollinations API returned ${response.status}: ${response.statusText}`);
      }

      // Check if image has content (prevent empty images)
      const contentLength = response.headers.get('content-length');
      console.log('[PollinationsService] Content-Length:', contentLength);

      if (contentLength && parseInt(contentLength) === 0) {
        throw new Error('Pollinations API returned empty image');
      }

      console.log('[PollinationsService] ===== IMAGE GENERATION SUCCESS =====');
      console.log('[PollinationsService] Generated image URL:', imageUrl);

      // Download and upload image to Supabase storage
      // Wrap in try-catch so upload failure doesn't break the whole flow
      try {
        const supabaseImageUrl = await this.uploadImageToSupabase(imageUrl, prompt);
        if (supabaseImageUrl) return supabaseImageUrl;
      } catch (uploadError) {
        console.warn('[PollinationsService] Supabase upload failed, using original URL:', uploadError);
      }

      return imageUrl;

    } catch (error) {
      console.error('[PollinationsService] ===== IMAGE GENERATION FAILED =====');
      console.error('[PollinationsService] Error details:', error);
      console.error('[PollinationsService] Prompt that failed:', prompt);

      // Return a base64 data URL as fallback to avoid DNS issues
      const fallbackSvg = this.createFallbackImage(prompt, options);
      console.log('[PollinationsService] Using fallback SVG image');
      return fallbackSvg;
    }
  }

  /**
   * Upload image from Pollinations to Supabase storage
   */
  private async uploadImageToSupabase(imageUrl: string, prompt: string): Promise<string | null> {
    try {
      console.log('[PollinationsService] Downloading image from Pollinations:', imageUrl);

      // Download the image from Pollinations
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
      }

      const imageBlob = await response.blob();

      // Generate a unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileName = `generated/${timestamp}_${randomId}.${imageBlob.type.split('/')[1] || 'png'}`;

      console.log('[PollinationsService] Uploading to Supabase storage:', fileName);

      // Upload to Supabase storage
      const uploadResult = await storageService.uploadImageToStorage(imageBlob, fileName);

      if (uploadResult?.publicUrl) {
        console.log('[PollinationsService] Successfully uploaded to Supabase:', uploadResult.publicUrl);
        return uploadResult.publicUrl;
      } else {
        console.warn('[PollinationsService] Failed to upload to Supabase, using original URL');
        return null;
      }

    } catch (error) {
      console.error('[PollinationsService] Error uploading to Supabase:', error);
      return null; // Return null to use original URL as fallback
    }
  }

  /**
   * Create a fallback SVG image as base64 data URL
   */
  private createFallbackImage(prompt: string, options: PollinationsGenerationOptions): string {
    const width = options.width || 512;
    const height = options.height || 512;
    const shortPrompt = prompt.length > 50 ? prompt.substring(0, 47) + '...' : prompt;

    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#1a1a1a;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#2d2d2d;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#bg)" />
        <text x="50%" y="45%" text-anchor="middle" fill="#ffffff" font-family="Arial, sans-serif" font-size="14" opacity="0.8">
          Image Generation
        </text>
        <text x="50%" y="55%" text-anchor="middle" fill="#cccccc" font-family="Arial, sans-serif" font-size="12" opacity="0.6">
          ${shortPrompt}
        </text>
        <text x="50%" y="65%" text-anchor="middle" fill="#888888" font-family="Arial, sans-serif" font-size="10" opacity="0.4">
          Failed to Load
        </text>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * Create a cache key from prompt and options
   */
  private createCacheKey(prompt: string, style?: string, options?: PollinationsGenerationOptions): string {
    const content = `${prompt}_${style || ''}_${JSON.stringify(options || {})}`;
    return `pollinations_${this.hashString(content)}`;
  }

  /**
   * Hash a string to create a consistent key
   */
  private hashString(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Enhance prompt for thumbnail generation
   */
  private enhancePromptForThumbnail(prompt: string, type: string): string {
    const baseEnhancement = "digital art, high quality, detailed, atmospheric";

    switch (type) {
      case 'story':
        return `${prompt}, ${baseEnhancement}, cinematic scene, mood lighting`;
      case 'cartridge':
        return `${prompt}, ${baseEnhancement}, game art style, fantasy setting`;
      case 'character':
        return `${prompt}, ${baseEnhancement}, character portrait, expressive`;
      default:
        return `${prompt}, ${baseEnhancement}`;
    }
  }

  /**
   * Create character portrait prompt
   */
  private createCharacterPrompt(name: string, archetype?: string, traits?: string[], appearanceDescription?: string): string {
    // Build prompt parts conditionally
    const parts = [`Portrait of ${name || 'character'}`];

    if (archetype && archetype.trim()) {
      parts.push(`a ${archetype}`);
    }

    if (appearanceDescription && appearanceDescription.trim()) {
      console.log('[PollinationsService] Using user appearance description:', appearanceDescription);
      parts.push(appearanceDescription);
    }

    if (traits && traits.length > 0) {
      parts.push(traits.join(', '));
    } else if (!appearanceDescription || !appearanceDescription.trim()) {
      parts.push('mysterious'); // Only add default if no user description
    }

    // Add standard style elements
    parts.push('cyberpunk fantasy style, digital art, high quality, detailed face, atmospheric lighting, futuristic setting');

    return parts.join(', ');
  }

  /**
   * Create story scene prompt
   */
  private createStoryPrompt(narrative: string, setting: string, mood: string): string {
    // Extract key visual elements from narrative
    const visualKeywords = this.extractVisualKeywords(narrative);
    return `${setting}, ${mood} atmosphere, ${visualKeywords}, cyberpunk fantasy, digital art, cinematic lighting, highly detailed, immersive scene`;
  }

  /**
   * Extract visual keywords from narrative text
   */
  private extractVisualKeywords(narrative: string): string {
    // Simple keyword extraction - in a real implementation, you might use NLP
    const keywords = narrative
      .toLowerCase()
      .match(/\b(dark|bright|neon|shadow|light|fire|water|metal|glass|crystal|ancient|modern|ruined|gleaming|mysterious|ominous|beautiful|terrifying)\w*\b/g) || [];

    return keywords.slice(0, 5).join(', ') || 'atmospheric scene';
  }

  /**
   * Generate text using Pollinations text models
   * @param prompt - Input text prompt
   * @param options - Text generation options
   * @returns Promise<string> - Generated text response
   */
  /**
   * Generate text using OpenRouter (removed Pollinations text to fix CORS)
   * @deprecated This method is deprecated - use OpenRouter service directly
   */
  async generateText(prompt: string, options: PollinationsTextOptions = {}): Promise<string> {
    console.warn('[PollinationsService] generateText is deprecated - use OpenRouter service directly to avoid CORS issues');

    // Import openRouterService dynamically to avoid circular dependency
    try {
      const { generateSimpleResponse } = await import('./openRouterService');
      return await generateSimpleResponse(prompt);
    } catch (error) {
      console.error('[PollinationsService] Fallback to OpenRouter failed:', error);
      return 'Text generation temporarily unavailable. Please use OpenRouter service directly.';
    }
  }

  /**
   * Generate text using OpenAI format (redirected to OpenRouter to fix CORS)
   * @deprecated This method is deprecated - use OpenRouter service directly
   */
  async generateTextOpenAI(messages: any[], options: PollinationsTextOptions = {}): Promise<any> {
    console.warn('[PollinationsService] generateTextOpenAI is deprecated - use OpenRouter service directly to avoid CORS issues');

    // Import openRouterService dynamically to avoid circular dependency
    try {
      const { generateSimpleResponse } = await import('./openRouterService');
      // Convert messages to prompt if needed
      const prompt = messages.map(m => m.content).join('\n');
      const text = await generateSimpleResponse(prompt);
      return {
        choices: [{
          message: {
            content: text
          }
        }]
      };
    } catch (error) {
      console.error('[PollinationsService] Fallback to OpenRouter failed:', error);
      return {
        choices: [{
          message: {
            content: 'Text generation temporarily unavailable. Please use OpenRouter service directly.'
          }
        }]
      };
    }
  }

  /**
   * Generate speech from text using text-to-speech
   * @param text - Text to convert to speech
   * @param options - Audio generation options
   * @returns Promise<string> - URL of the generated audio
   */
  async generateSpeech(
    text: string,
    options: PollinationsAudioOptions = {},
    userId?: string
  ): Promise<string> {
    const startTime = Date.now();

    try {
      const defaultVoice = 'nova'; // Good general voice
      const voice = options.voice || defaultVoice;

      if (!this.availableVoices.includes(voice)) {
        console.warn(`[Pollinations] Voice "${voice}" not available, using default "${defaultVoice}"`);
      }

      // Ensure text is not too long for URL encoding
      const maxTextLength = 200; // Conservative limit for URL
      const truncatedText = text.length > maxTextLength ? text.substring(0, maxTextLength) + '...' : text;

      // Use the text API with audio model for TTS
      const audioUrl = `${this.baseTextUrl}/openai/${encodeURIComponent(truncatedText)}?model=openai-audio&voice=${voice}&referrer=${this.referrer}`;

      // For frontend calls, use referrer instead of Authorization header to avoid CORS
      console.log('[PollinationsService] Using referrer-based authentication for TTS');

      // Test if the audio generates properly
      const response = await fetch(audioUrl, {
        method: 'HEAD'
      });
      if (!response.ok) {
        throw new Error(`TTS generation failed: ${response.status}`);
      }

      // Log API usage
      const responseTime = Date.now() - startTime;
      if (userId) {
        await databaseService.logApiUsage('pollinations', 'text-to-speech', text.length, 0);
      }

      console.log(`[Pollinations] TTS generated successfully in ${responseTime}ms for "${truncatedText}"`);
      return audioUrl;

    } catch (error) {
      console.error('[Pollinations] TTS generation error:', error);
      throw error;
    }
  }

  /**
   * Generate speech using POST method for longer texts
   * @param text - Text to convert to speech
   * @param options - Audio generation options
   * @returns Promise<any> - Audio data response
   */
  async generateSpeechPOST(text: string, options: PollinationsAudioOptions = {}): Promise<any> {
    const { voice = 'nova', model = 'openai-audio' } = options;

    try {
      const response = await fetch(`${this.baseTextUrl}/openai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model,
          messages: [{ role: 'user', content: text }],
          voice
        })
      });

      if (!response.ok) {
        throw new Error(`Pollinations speech API error: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('[Pollinations] Speech generation error:', error);
      throw error;
    }
  }

  /**
   * Transcribe audio to text (speech-to-text)
   * @param audioFile - Audio file to transcribe
   * @returns Promise<string> - Transcribed text
   */
  async transcribeAudio(
    audioBlob: Blob,
    userId?: string
  ): Promise<string> {
    const startTime = Date.now();

    try {
      // For now, use a placeholder implementation
      // In a real implementation, you'd upload the audio to Pollinations STT endpoint

      console.log('[Pollinations] STT not yet implemented, returning placeholder');

      // Log API usage
      const responseTime = Date.now() - startTime;
      if (userId) {
        await databaseService.logApiUsage('pollinations', 'speech-to-text', 1, 0);
      }

      return "Speech-to-text transcription would appear here";

    } catch (error) {
      console.error('[Pollinations] STT error:', error);
      throw error;
    }
  }

  /**
   * List available image models
   * @returns Promise<string[]> - Array of available models
   */
  async getImageModels(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseImageUrl}/models`);

      if (!response.ok) {
        throw new Error(`Pollinations models API error: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('[Pollinations] Error fetching image models:', error);
      return ['flux', 'turbo']; // Default models
    }
  }

  /**
   * List available text models
   * @returns Promise<string[]> - Array of available models
   */
  async getTextModels(): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseTextUrl}/models`);

      if (!response.ok) {
        throw new Error(`Pollinations models API error: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('[Pollinations] Error fetching text models:', error);
      return ['openai', 'llama', 'mistral', 'gemini']; // Default models
    }
  }

  /**
   * List available audio voices
   * @returns string[] - Array of available voices
   */
  getAvailableVoices(): string[] {
    return [...this.availableVoices];
  }

  /**
   * Test the service by generating a simple image
   * @returns Promise<boolean> - True if service is working
   */
  async testService(): Promise<boolean> {
    try {
      const testImageUrl = await this.generateImageFromPollinations('test cyberpunk robot', { width: 100, height: 100 });
      return testImageUrl.includes('pollinations.ai');
    } catch (error) {
      console.error('[Pollinations] Service test failed:', error);
      return false;
    }
  }

  // Get voice for specific companion
  getCompanionVoice(companionName: string): string {
    const voiceMap: Record<string, string> = {
      'JAX': 'onyx',      // Deep, tech-focused voice
      'Lyra': 'nova',     // Smooth, philosophical voice
      'System': 'echo',   // Neutral system voice
      'Narrator': 'fable' // Storytelling voice
    };

    return voiceMap[companionName] || 'nova';
  }

  // Test Pollinations connection
  async testConnection(): Promise<boolean> {
    try {
      const testUrl = await this.generateImageFromPollinations('test', { width: 64, height: 64 });
      return testUrl.includes('pollinations.ai');
    } catch (error) {
      console.error('[Pollinations] Connection test failed:', error);
      return false;
    }
  }

  // Batch generate images for story segments
  async generateBatchImages(
    prompts: string[],
    options: PollinationsGenerationOptions = {},
    userId?: string
  ): Promise<string[]> {
    const results: string[] = [];

    for (const prompt of prompts) {
      try {
        const imageUrl = await this.generateImageFromPollinations(prompt, { ...this.defaultOptions, ...options });
        results.push(imageUrl);

        // Add small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`[Pollinations] Failed to generate image for prompt: ${prompt}`, error);
        results.push(''); // Empty string for failed generation
      }
    }

    return results;
  }

  // Generate companion dialogue audio
  async generateCompanionAudio(
    companionName: string,
    dialogue: string,
    userId?: string
  ): Promise<string> {
    const voice = this.getCompanionVoice(companionName);
    return this.generateSpeech(dialogue, { voice }, userId);
  }

  /**
   * Clear user's image cache
   */
  async clearUserCache(userId: string): Promise<void> {
    await databaseService.clearImageCache(userId);
  }
}

// Export both the class and singleton instance
export { PollinationsService };
export const pollinationsService = PollinationsService.getInstance();
export default pollinationsService; 